import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Star, Quotes, SealCheck } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initials = (name) => (name || '?').split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

/**
 * Landing-page testimonials pulled from REAL 5-star reviews (/api/reviews/testimonials).
 * Renders nothing if there are no real reviews yet — never fabricates.
 * variant="grid" (default) or "single" (compact card, shows first testimonial).
 */
const RealTestimonials = ({ variant = 'grid', limit = 6, heading = 'What Our Community Says' }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/reviews/testimonials`, { params: { limit } })
      .then((res) => setItems(res.data?.testimonials || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [limit]);

  if (loading || items.length === 0) return null;

  if (variant === 'single') {
    const t = items[0];
    return (
      <div className="bg-white dark:bg-[#0F172A] border-2 border-black dark:border-white shadow-[4px_4px_0px_#000] p-4" data-testid="real-testimonial-single">
        <Quotes size={24} weight="fill" className="text-[#2B4C6F] mb-3" />
        <p className="text-neutral-700 dark:text-neutral-300 text-sm mb-3 italic">"{t.content}"</p>
        <div className="flex items-center gap-2">
          <div className="flex text-amber-400">
            {[...Array(t.rating)].map((_, i) => <Star key={i} size={14} weight="fill" />)}
          </div>
          <span className="text-xs text-neutral-500">
            {t.reviewer_name ? `${t.reviewer_name} → ` : ''}{t.author_name}
            {t.verified && <SealCheck size={12} weight="fill" className="inline ml-1 text-[#2B4C6F]" />}
          </span>
        </div>
      </div>
    );
  }

  return (
    <section className="py-16 bg-[#E8E9EB] dark:bg-[#121212]" data-testid="real-testimonials">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-black uppercase tracking-tight text-neutral-900 dark:text-white">{heading}</h2>
          <p className="text-neutral-500 mt-2">Real reviews from real projects on ConstructHelp.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((t) => (
            <div key={t._id} className="bg-white dark:bg-[#0F172A] border-4 border-black shadow-[6px_6px_0px_#000] p-5 flex flex-col" data-testid={`testimonial-card-${t._id}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex text-amber-400">
                  {[...Array(t.rating)].map((_, i) => <Star key={i} size={16} weight="fill" />)}
                </div>
                {t.verified && (
                  <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest bg-[#2B4C6F] text-white px-1.5 py-0.5 border border-black">
                    <SealCheck size={10} weight="fill" /> Verified Hire
                  </span>
                )}
              </div>
              {t.title && <p className="font-black text-neutral-900 dark:text-white mb-1">{t.title}</p>}
              <p className="text-sm text-neutral-700 dark:text-neutral-300 leading-relaxed flex-1">"{t.content}"</p>
              <div className="flex items-center gap-3 mt-4 pt-3 border-t-2 border-neutral-200 dark:border-neutral-700">
                <div className="w-10 h-10 flex-shrink-0 bg-[#D95800] text-white font-black flex items-center justify-center border-2 border-black">
                  {initials(t.author_name)}
                </div>
                <div className="min-w-0">
                  <p className="font-black text-sm text-neutral-900 dark:text-white truncate">{t.author_name}</p>
                  <p className="text-xs text-neutral-500 truncate">{t.author_role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default RealTestimonials;
